import { translate } from "@classdojo/web/utils/translate";
import combineActionHandlers from "@web-monorepo/infra/combineActionHandlers";
import createAction from "@web-monorepo/infra/createAction";
import createActionCreator from "@web-monorepo/infra/createActionCreator";
import createReducer from "@web-monorepo/infra/createReducer";
import { PodInstallFunction } from "@web-monorepo/shared/podInfra";
import icon from "./assets/logo.svg";
import { Student } from "src/pods/students";
import { AppMetadata } from "src/pods/toolkitData/components/EditableSetup";
import setupFetch from "src/utils/setupFetch";
import setupPut from "src/utils/setupPut";

const STATE_KEY = "groups";

export type Group = {
  students: Student[];
};

export const appMetadataGroups = (): AppMetadata => ({
  href: "groups",
  title: translate("dojo.toolkit:apps.group_maker"),
  backgroundColor: "dt_content_danger",
  icon,
});

export type GroupsSlice = {
  [STATE_KEY]: GroupsState;
};

export type GroupsState = {
  baseAvatarURL: string | null;
  groups: Group[] | null;
  restrictions: unknown;
};

const initialState: GroupsState = {
  baseAvatarURL: null,
  groups: null,
  restrictions: null,
};

export const selectBaseAvatarURL = (state: GroupsSlice) => state?.[STATE_KEY]?.baseAvatarURL;
export const selectGroups = (state: GroupsSlice) => state?.[STATE_KEY]?.groups;
export const selectGroupRestrictions = (state: GroupsSlice) => state?.[STATE_KEY]?.restrictions;

export const SEND_GROUPS = createAction("SEND_GROUPS");
export const sendGroups = createActionCreator(SEND_GROUPS, (groups: Group[]) => ({ groups }));

export const RECEIVE_GROUPS = createAction("RECEIVE_GROUPS");
export const receiveGroupsReducer = createReducer(
  RECEIVE_GROUPS,
  ({ baseAvatarURL, groups }: { baseAvatarURL: string; groups: Group[] }) => ({
    baseAvatarURL,
    groups,
  }),
);

const {
  actionCreator: fetchGroupRestrictions,
  saga: fetchGroupRestrictionsSaga,
  reducer: fetchGroupRestrictionsReducer,
} = setupFetch({
  stateKey: STATE_KEY,
  makePath: ({ classId }: { classId: string }) => `/api/userConfig/metadata/toolkitGroupRestrictions_${classId}`,
  reducer:
    (payload: { response: { value: string[] } }) =>
    (state: GroupsState): GroupsState => ({
      ...state,
      restrictions: payload.response.value || [],
    }),
});

export { fetchGroupRestrictions };

const {
  actionCreator: editGroupRestrictions,
  saga: editGroupRestrictionsSaga,
  reducer: editGroupRestrictionsReducer,
} = setupPut({
  stateKey: STATE_KEY,
  makePath: ({ classId }: { classId: string }) => `/api/userConfig/metadata/toolkitGroupRestrictions_${classId}`,
  makeBody: ({ groupOfStudentIdGroups }: { groupOfStudentIdGroups: string[] }) => ({ value: groupOfStudentIdGroups }),
  reducer:
    (payload: { groupOfStudentIdGroups: string[] }) =>
    (state: GroupsState): GroupsState => ({
      ...state,
      restrictions: payload.groupOfStudentIdGroups,
    }),
});

export { editGroupRestrictions };

const reducer = combineActionHandlers(initialState, [
  fetchGroupRestrictionsReducer,
  editGroupRestrictionsReducer,
  receiveGroupsReducer,
]);

const install: PodInstallFunction = (installReducer, installSaga) => {
  installReducer(STATE_KEY, reducer);
  installSaga(fetchGroupRestrictionsSaga);
  installSaga(editGroupRestrictionsSaga);
};

export default install;
